import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Lightbulb } from 'lucide-react';

interface UseCase {
  id: string;
  title: string;
  description: string;
}

interface UseCaseComparisonProps {
  currentUseCases: UseCase[];
  futureUseCases: UseCase[];
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

interface UseCaseContainerProps {
  isFuture: boolean;
}

const UseCaseContainer = styled(Box)<UseCaseContainerProps>(({ theme, isFuture }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  background: isFuture ? 'rgba(0, 204, 153, 0.05)' : 'rgba(138, 43, 226, 0.05)',
  borderRadius: theme.spacing(1),
  border: `1px solid ${isFuture ? 'rgba(0, 204, 153, 0.2)' : 'rgba(138, 43, 226, 0.2)'}`,
  '&:last-child': {
    marginBottom: 0,
  },
}));

interface UseCaseTitleProps {
  isFuture: boolean;
}

const UseCaseTitle = styled(Typography)<UseCaseTitleProps>(({ theme, isFuture }) => ({
  color: isFuture ? '#00CC99' : '#8A2BE2',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const UseCaseComparison: React.FC<UseCaseComparisonProps> = ({
  currentUseCases,
  futureUseCases,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Lightbulb size={20} color="#8A2BE2" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Use Case Comparison
        </Typography>
      </Box>

      <SectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                Current Use Cases
              </Typography>

              {currentUseCases.map((useCase) => (
                <UseCaseContainer key={useCase.id} isFuture={false}>
                  <UseCaseTitle variant="subtitle1" isFuture={false}>
                    {useCase.title}
                  </UseCaseTitle>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {useCase.description}
                  </Typography>
                </UseCaseContainer>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                Future Use Cases
              </Typography>

              {futureUseCases.map((useCase) => (
                <UseCaseContainer key={useCase.id} isFuture={true}>
                  <UseCaseTitle variant="subtitle1" isFuture={true}>
                    {useCase.title}
                  </UseCaseTitle>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {useCase.description}
                  </Typography>
                </UseCaseContainer>
              ))}
            </Box>
          </Grid>
        </Grid>
      </SectionContainer>
    </Box>
  );
};

export default UseCaseComparison; 